import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Chip,
  Collapse
} from '@mui/material';
import { 
  SmartToy as RobotIcon, 
  Code as SoftwareIcon, 
  Settings as HybridIcon, 
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Build as MaintenanceIcon,
  Error as ErrorIcon,
  CalendarMonth as CalendarIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { widgetService } from '../services/widgetService';
import { 
  BarChart, 
  Bar, 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell 
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Status color mapping
const statusColors = {
  active: '#4caf50',
  inactive: '#9e9e9e',
  maintenance: '#ff9800',
  error: '#f44336'
};

// Dashboard color palette
const dashboardColors = {
  primary: '#2196f3',    // Blu
  success: '#4caf50',    // Verde
  warning: '#ff9800',    // Arancione
  error: '#f44336',      // Rosso
  secondary: '#9c27b0',  // Viola
  info: '#00bcd4',       // Azzurro
  grey: '#757575'        // Grigio
};

// Generate random color
const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'active':
      return <ActiveIcon sx={{ color: statusColors.active }} />;
    case 'inactive':
      return <InactiveIcon sx={{ color: statusColors.inactive }} />;
    case 'maintenance':
      return <MaintenanceIcon sx={{ color: statusColors.maintenance }} />;
    case 'error':
      return <ErrorIcon sx={{ color: statusColors.error }} />;
    default:
      return null;
  }
};

const TypeIcon = ({ type }) => {
  switch (type) {
    case 'robot':
      return <RobotIcon />;
    case 'software':
      return <SoftwareIcon />;
    case 'hybrid':
      return <HybridIcon />;
    default:
      return null;
  }
};

const Dashboard = () => {
  const [widgets, setWidgets] = useState([]);
  const [widgetPayloads, setWidgetPayloads] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stati per filtri e paginazione
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(dayjs().subtract(6, 'month'));
  const [endDate, setEndDate] = useState(dayjs());
  
  // Stati per la paginazione delle tabelle
  const [codexPage, setCodexPage] = useState(0);
  const [codexRowsPerPage, setCodexRowsPerPage] = useState(5);
  const [processPage, setProcessPage] = useState(0);
  const [processRowsPerPage, setProcessRowsPerPage] = useState(5);

  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    const fetchWidgetsAndPayloads = async () => {
      setLoading(true);
      try {
        const widgetsList = await widgetService.getAll();
        setWidgets(widgetsList);
        const payloads = {};
        await Promise.all(
          widgetsList.map(async (w) => {
            const data = await widgetService.getData(w.id, {
              // Convert local dates to UTC for backend
              date_from: startDate ? dayjs(startDate).utc().format('YYYY-MM-DD') : undefined,
              date_to: endDate ? dayjs(endDate).utc().format('YYYY-MM-DD') : undefined,
            });
            console.log('Widget data:', w.code, data);
            payloads[w.id] = data;
          })
        );
        setWidgetPayloads(payloads);
        setError(null);
      } catch (err) {
        console.error('Error fetching widgets:', err);
        setError('Errore nel caricamento widget');
      } finally {
        setLoading(false);
      }
    };
    fetchWidgetsAndPayloads();
  }, [startDate, endDate]);

  // Gestori eventi
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };
  
  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCodexPageChange = (event, newPage) => {
    setCodexPage(newPage);
  };
  
  const handleCodexRowsPerPageChange = (event) => {
    setCodexRowsPerPage(parseInt(event.target.value, 10));
    setCodexPage(0);
  };
  
  const handleProcessPageChange = (event, newPage) => {
    setProcessPage(newPage);
  };
  
  const handleProcessRowsPerPageChange = (event) => {
    setProcessRowsPerPage(parseInt(event.target.value, 10));
    setProcessPage(0);
  };

  const handleExportData = () => {
    alert('Export functionality would be implemented here');
  };

  // Formattazione della data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };
  
  // Ottenere il colore in base allo stato
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  // Aggregazione dati per grafico/statistiche
  const aggregated = {};
  widgets.forEach(widget => {
    const payload = widgetPayloads[widget.id]?.payload;
    if (payload && typeof payload === 'object') {
      Object.entries(payload).forEach(([dataset, data]) => {
        if (!aggregated[dataset]) aggregated[dataset] = { count: 0, error: 0 };
        aggregated[dataset].count += data.count || 0;
        aggregated[dataset].error += data.error || 0;
      });
    }
  });
  const chartData = Object.entries(aggregated).map(([dataset, data]) => ({
    dataset,
    ...data
  }));

  // Utility: get all months in range [start, end] (inclusive)
  function getMonthsInRange(start, end) {
    const result = [];
    let current = start.startOf('month');
    const last = end.startOf('month');
    while (current.isBefore(last) || current.isSame(last)) {
      result.push(current.format('YYYY-MM'));
      current = current.add(1, 'month');
    }
    return result;
  }

  const renderWidget = (widget, payload) => {
    if (!widget) {
      return null;
    }

    switch (widget.code) {
      case 'totale_codex':
        const totaleCodexData = payload?.totale_codex?.[0].totals;
        return (
          <Card
            sx={{
              background: '#fff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(60,60,60,0.06)',
              border: '1px solid #eee',
              py: 0.5,
              px: 1.5,
              minHeight: 160,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              fontSize: '2rem'
            }}
          >
            <Typography variant="subtitle1" color="grey.700" sx={{ mb: 1 }}>
              Totale Codex
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2rem', color: 'primary.main' }}>
              {totaleCodexData ?? 0}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
              
            </Typography>
          </Card>
        );
      case 'codex_processati':
        const processatiData = payload?.codex_processati?.[0]?.codex_completed;
        return (
          <Card
            sx={{
              background: '#fff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(60,60,60,0.06)',
              border: '1px solid #eee',
              py: 0.5,
              px: 1.5,
              minHeight: 160,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              fontSize: '2rem'
            }}
          >
            <Typography variant="subtitle1" color="grey.700" sx={{ mb: 1 }}>
              Codex Processati
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2rem', color: 'success.main' }}>
              {processatiData ?? 0}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
              
            </Typography>
          </Card>
        );
      case 'errori':
        const erroriData = payload?.errori?.[0].codex_failed;
        return (
          <Card
            sx={{
              background: '#fff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(60,60,60,0.06)',
              border: '1px solid #eee',
              py: 0.5,
              px: 1.5,
              minHeight: 160,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              fontSize: '2rem'
            }}
          >
            <Typography variant="subtitle1" color="grey.700" sx={{ mb: 1 }}>
              Codex con Errori
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'error.main', fontSize: '2rem' }}>
              {erroriData ?? 0}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
              
            </Typography>
          </Card>
        );
      case 'andamento_codex':
        let barData = payload?.andamento_codex || [];
        // Show only months in Da/A range, fill missing with 0
        const monthsInRange = getMonthsInRange(startDate, endDate);
        barData = monthsInRange.map(month =>
          barData.find(d => d.month === month) || { month, totals: 0, completed: 0, failed: 0 }
        );
        return (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Andamento Codex</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" interval={0} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="totals" name="Totale" fill={dashboardColors.primary} />
                <Bar dataKey="completed" name="Completati" fill={dashboardColors.success} />
                <Bar dataKey="failed" name="Errori" fill={dashboardColors.error} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        );
      case 'distribuzione_codex':
        const pieData = payload?.distribuzione_codex || [];
        // Extended modern palette
        const pieColors = [
          '#1976d2', // blu
          '#388e3c', // verde
          '#fbc02d', // giallo
          '#d32f2f', // rosso
          '#7b1fa2', // viola
          '#0288d1', // azzurro
          '#c2185b', // rosa
          '#ffa000', // arancione
          '#455a64', // grigio scuro
          '#8bc34a', // verde chiaro
          '#e64a19', // arancio scuro
          '#00acc1', // turchese
          '#f06292', // rosa chiaro
          '#afb42b', // lime scuro
          '#5d4037', // marrone
          '#0097a7', // teal
          '#f44336', // rosso acceso
          '#7e57c2', // viola chiaro
          '#43a047', // verde intenso
          '#ffd600', // giallo acceso
        ];
        return (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Distribuzione Tipi di Codex</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="type"
                  label
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        );
      case 'andamento_processi':
        let lineData = payload?.andamento_processi || [];
        // Show only months in Da/A range, fill missing with 0
        const monthsInRange2 = getMonthsInRange(startDate, endDate);
        lineData = monthsInRange2.map(month =>
          lineData.find(d => d.month === month) || { month, totals: 0, completed: 0, failed: 0 }
        );
        return (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Andamento Processi</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" interval={0} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="totals" name="Totale" stroke={dashboardColors.primary} />
                <Line type="monotone" dataKey="completed" name="Completati" stroke={dashboardColors.success} />
                <Line type="monotone" dataKey="failed" name="Errori" stroke={dashboardColors.error} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        );
      default:
        return (
          <Card>
            <CardHeader title={widget.name || widget.id} />
            <CardContent>
              <Typography variant="subtitle2">Payload</Typography>
              <pre style={{ fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                {JSON.stringify(payload, null, 2)}
              </pre>
            </CardContent>
          </Card>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      {/* Date filter: compact and collapsible */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<CalendarIcon />}
          size="small"
          variant="outlined"
          onClick={() => setShowDateFilter(v => !v)}
        >
          {`Filtra per data: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
        </Button>
        <Collapse in={showDateFilter}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Da"
                value={startDate ? dayjs(startDate) : dayjs().subtract(6, 'month')}
                onChange={val => setStartDate(val)}
                maxDate={endDate ? dayjs(endDate) : dayjs()}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="A"
                value={endDate ? dayjs(endDate) : dayjs()}
                onChange={val => setEndDate(val)}
                minDate={startDate ? dayjs(startDate) : dayjs().subtract(6, 'month')}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
        </Collapse>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Widget cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {widgets
          .filter(widget =>
            widget.code === 'totale_codex' ||
            widget.code === 'codex_processati' ||
            widget.code === 'errori'
          )
          .map(widget => (
            <Grid item xs={12} md={4} key={widget.id}>
              {renderWidget(widget, widgetPayloads[widget.id]?.payload)}
            </Grid>
        ))}
      </Grid>

      {/* Andamento Codex (2/3) + Distribuzione Tipi di Codex (1/3) */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          {(() => {
            const widget = widgets.find(w => w.code === 'andamento_codex');
            return renderWidget(widget, widgetPayloads[widget?.id]?.payload);
          })()}
        </Grid>
        <Grid item xs={12} md={4}>
          {(() => {
            const widget = widgets.find(w => w.code === 'distribuzione_codex');
            return renderWidget(widget, widgetPayloads[widget?.id]?.payload);
          })()}
        </Grid>
      </Grid>

      {/* Andamento Processi - full width below all other widgets */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {(() => {
            const widget = widgets.find(w => w.code === 'andamento_processi');
            return renderWidget(widget, widgetPayloads[widget?.id]?.payload);
          })()}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 